import { html, until } from "@umbraco-cms/backoffice/external/lit";
import { interceptLitElement } from "../utils/lit";
import { hookPrototypeFunction } from "../utils/hooks";
import type { UmbTableColumn } from '@umbraco-cms/backoffice/components';
import type { UmbEditableDocumentCollectionItemModel } from '@umbraco-cms/backoffice/document';
import { UmbDocumentTypeItemRepository } from '@umbraco-cms/backoffice/document-type';
import type { UmbControllerHost } from '@umbraco-cms/backoffice/controller-api';
import { CustomCatalogueDropdownService, type CategoryDropdownDto } from '@api';
import type { CategorySelection } from '@models';

const nameCache = new WeakMap<object, Map<string, Promise<string>>>();
let categoriesPromise: Promise<CategoryDropdownDto[]> | null = null;

// For collections (formerly known as 'list views') of documents, we have some custom columns of 
// which the values need to be dynamically adjusted. This hook accomplishes that.
interceptLitElement("umb-document-table-column-property-value", constructor => {
    hookPrototypeFunction(constructor, "render", function (next) {
        const element = this as UmbDocumentTableColumnPropertyValueElement;

        switch (element.column.alias) {
            // For the 'Type' column, show the NAME of the content type, rather than the alias
            case "contentTypeAlias": {
                const unique = element.value.item.documentType.unique;

                let elementCache = nameCache.get(element);
                if (!elementCache) {
                    elementCache = new Map();
                    nameCache.set(element, elementCache);
                }

                if (!elementCache.has(unique)) {
                    const repo = new UmbDocumentTypeItemRepository(element as unknown as UmbControllerHost);
                    const promise = repo.requestItems([unique]).then(result => result.data?.[0]?.name ?? unique);
                    elementCache.set(unique, promise);
                }

                return html`${until(elementCache.get(unique), '')}`;
            }
            // For the 'Category' column, show the area NAME of the category, rather than the area ID. And if the name can't
            // be found, it means the category is no longer valid, so in that case show '-' instead of the raw ID.
            case "categorySelection": {
                const raw = element.value.item.values?.find(v => v.alias === "categorySelection")?.value;
                if (!raw) {
                    return html`-`;
                }

                const parsed: CategorySelection = typeof raw === 'string' ? JSON.parse(raw) : raw as CategorySelection;
                if (!parsed.areaId) {
                    return html`-`;
                }

                const areaId = parsed.areaId;
                const promise = getCategories().then(categories => {
                    const match = categories.find(c => c.areaId === areaId);
                    return match?.areaName ?? '-';
                });

                return html`${until(promise, '')}`;
            }
            default: {
                return next();
            }
        }
    });

    return constructor;
});

function getCategories(): Promise<CategoryDropdownDto[]> {
    if (!categoriesPromise) {
        categoriesPromise = CustomCatalogueDropdownService
            .getCategoryDropdown({
                query: {
                    getAllCatalogues: true
                }
            })
            .then((response: any) => (response.data as CategoryDropdownDto[]) ?? [])
            .catch(() => []);
    }

    return categoriesPromise!;
}

// Umbraco doesn't export this element, so we'll create a basic interface for it ourselves
interface UmbDocumentTableColumnPropertyValueElement {
    column: UmbTableColumn;
    value: UmbEditableDocumentCollectionItemModel;
}

